import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, Menu, X } from 'lucide-react';
import DeliveryList from './controltower/DeliveryList';
import DeliveryModal from './controltower/DeliveryModal';
import StatusFooter from './controltower/StatusFooter';

const districts = {
  "Ate": { latitude: -12.037, longitude: -76.955 },
  "Barranco": { latitude: -12.145, longitude: -77.021 },
  "Breña": { latitude: -12.063, longitude: -77.049 },
  "Carabayllo": { latitude: -11.884, longitude: -77.038 },
  "Chorrillos": { latitude: -12.178, longitude: -77.001 },
  "Comas": { latitude: -11.936, longitude: -77.060 },
  "El Agustino": { latitude: -12.047, longitude: -76.997 },
  "Independencia": { latitude: -11.982, longitude: -77.049 },
  "Jesús María": { latitude: -12.076, longitude: -77.049 },
  "La Molina": { latitude: -12.073, longitude: -76.947 },
  "La Victoria": { latitude: -12.071, longitude: -77.013 },
  "Lince": { latitude: -12.087, longitude: -77.035 },
  "Los Olivos": { latitude: -11.981, longitude: -77.067 },
  "Magdalena del Mar": { latitude: -12.090, longitude: -77.069 },
  "Miraflores": { latitude: -12.120, longitude: -77.030 },
  "Pueblo Libre": { latitude: -12.078, longitude: -77.073 },
  "Puente Piedra": { latitude: -11.872, longitude: -77.069 },
  "Rimac": { latitude: -12.026, longitude: -77.026 },
  "San Borja": { latitude: -12.101, longitude: -77.001 },
  "San Isidro": { latitude: -12.096, longitude: -77.036 },
  "San Juan de Lurigancho": { latitude: -12.006, longitude: -76.988 },
  "San Juan de Miraflores": { latitude: -12.152, longitude: -76.963 },
  "San Luis": { latitude: -12.073, longitude: -77.000 },
  "San Martin de Porres": { latitude: -12.015, longitude: -77.059 },
  "San Miguel": { latitude: -12.080, longitude: -77.096 },
  "Santa Anita": { latitude: -12.052, longitude: -76.971 },
  "Santiago de Surco": { latitude: -12.140, longitude: -76.995 },
  "Surquillo": { latitude: -12.116, longitude: -77.022 },
  "Villa El Salvador": { latitude: -12.193, longitude: -76.949 },
  "Villa Maria del Triunfo": { latitude: -12.161, longitude: -76.944 },
  "Cercado de Lima": { latitude: -12.046, longitude: -77.031 },
};

function ControlTower({ userId, setActiveComponent }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  const [deliveryMarkers, setDeliveryMarkers] = useState({});
  const [driverMarkers, setDriverMarkers] = useState({});
  
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showList, setShowList] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Initialize Google Map
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current && window.google) {
      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: -12.0602, lng: -77.0371 },
        zoom: 12,
        disableDefaultUI: true,
        gestureHandling: 'greedy',
      });
    }
  }, []);

  useEffect(() => {
        const POLL_MS = 5000;
        let timer;
        let inFlight = false;
        let stopped = false;
        const ctrl = new AbortController();

        const tick = async () => {
            if (stopped || inFlight) return;   // prevent overlap & stop when unmounted
            inFlight = true;
            try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/delivery_points/provider/${userId}`,
                { signal: ctrl.signal }
            );
            if (!stopped) {
                // console.log("Fetched delivery points:", res.data);
                setDeliveryPoints(res.data);
            }
            } catch (err) {
            // ignore cancellation; log real errors
            if (!(err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED')) {
                console.error("Error fetching delivery points:", err);
            }
            } finally {
            inFlight = false;
            if (!stopped) timer = setTimeout(tick, POLL_MS); // schedule next AFTER finish
            }
        };

        tick(); // initial fetch

        return () => {
            stopped = true;
            ctrl.abort();           // cancels any in-flight request
            clearTimeout(timer);    // stops future ticks
        };
        }, [userId]);

  // Fetch drivers every 5 seconds
  useEffect(() => {
    async function fetchDrivers() {
      try {
        const driverIds = [
          ...new Set([
            ...deliveryPoints.map(dp => dp.driver_id).filter(Boolean),
          ])
        ];

        if (driverIds.length > 0) {
          const response = await axios.post(`${baseUrl}/api/drivers`, {
            driver_ids: driverIds
          });
          setDrivers(response.data.drivers || []);
        }
      } catch (error) {
        console.error('Error fetching drivers:', error);
      }
    }

    if (deliveryPoints.length > 0) {
      fetchDrivers();
      const intervalId = setInterval(fetchDrivers, 5000);
      return () => clearInterval(intervalId);
    }
  }, [deliveryPoints, baseUrl]);

  // Initialize delivery markers
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    Object.values(deliveryMarkers).forEach(marker => marker.setMap(null));
    const newMarkers = {};

    deliveryPoints.forEach(point => {
      let { latitude, longitude } = point;

      // Determine icon based on status
      let iconUrl;
      switch (point.status) {
        case 'pending':
          iconUrl = `${process.env.PUBLIC_URL}/icons/pin-orange.svg`;
          break;
        case 'entregado':
          iconUrl = `${process.env.PUBLIC_URL}/icons/pin-green.svg`;
          break;
        case 'cancelado':
        case 'rechazado':
          iconUrl = `${process.env.PUBLIC_URL}/icons/pin-red.svg`;
          break;
        case 'encamino':
          iconUrl = `${process.env.PUBLIC_URL}/icons/pin-blue.svg`;
          break;
        default:
          iconUrl = `${process.env.PUBLIC_URL}/icons/pin-grey.svg`;
      }

      // Use district coordinates if no exact location
      if (!latitude || !longitude) {
        const districtData = districts[point.district];
        if (districtData) {
          latitude = districtData.latitude + (Math.random() - 0.5) * 0.01;
          longitude = districtData.longitude + (Math.random() - 0.5) * 0.01;
        } else {
          return;
        }
      }

      const marker = new window.google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map: mapRef.current,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(30, 30),
        },
      });

      marker.addListener('click', () => {
        setSelectedDelivery(point);
        mapRef.current.panTo({ lat: latitude, lng: longitude });
        mapRef.current.setZoom(16);
      });

      newMarkers[point.id] = marker;
    });

    setDeliveryMarkers(newMarkers);
  }, [deliveryPoints]);

  // Initialize driver markers
  useEffect(() => {
    if (!mapRef.current) return;

    Object.values(driverMarkers).forEach(marker => marker.setMap(null));
    const newMarkers = {};

    drivers.forEach(driver => {
      if (driver.longitude && driver.latitude) {
        const marker = new window.google.maps.Marker({
          position: { lat: driver.latitude, lng: driver.longitude },
          map: mapRef.current,
          icon: {
            url: `${process.env.PUBLIC_URL}/logo.png`,
            scaledSize: new window.google.maps.Size(30, 30),
          },
        });

        newMarkers[driver.id] = marker;
      }
    });

    setDriverMarkers(newMarkers);
  }, [drivers]);

  function handleDeliveryClick(delivery) {
    setSelectedDelivery(delivery);
    
    // Pan to delivery location
    let lat = delivery.latitude;
    let lng = delivery.longitude;
    
    if (!lat || !lng) {
      const districtData = districts[delivery.district];
      if (districtData) {
        lat = districtData.latitude;
        lng = districtData.longitude;
      }
    }
    
    if (lat && lng && mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(16);
    }
  }

  const filteredDeliveries = deliveryPoints.filter(delivery => {
    const query = searchQuery.toLowerCase();
    return (
      delivery.recipient_name?.toLowerCase().includes(query) ||
      delivery.recipient_phone?.toLowerCase().includes(query) ||
      delivery.id?.toString().includes(query)
    );
  });

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-30">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            
            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Torre de Control
            </h1>

            <button
              onClick={() => setShowList(!showList)}
              className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors lg:hidden"
            >
              {showList ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="w-20 hidden lg:block"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Delivery List - Sidebar */}
        <DeliveryList
          deliveries={filteredDeliveries}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onDeliveryClick={handleDeliveryClick}
          showList={showList}
        />

        {/* Map Container */}
        <div className="flex-1 relative">
          <div ref={mapContainerRef} className="w-full h-full" />
        </div>
      </div>

      {/* Fixed Footer */}
      <StatusFooter deliveryPoints={deliveryPoints} />

      {/* Delivery Modal */}
      {selectedDelivery && (
        <DeliveryModal
          delivery={selectedDelivery}
          onClose={() => setSelectedDelivery(null)}
        />
      )}
    </div>
  );
}

export default ControlTower;